import type { SiteContent } from "./types.js";

const content: SiteContent = {
  meta: {
    title: "Kuori — the whole event, through the people in it",
    description:
      "Guests scan a QR code, shoot from the browser, and the photos land in the event album and on the live screen. No app to install.",
    whatsapp: "Hi! I came from the Kuori site and I'd like to know more.",
    photoAlt: {
      hero: "Guests on the dance floor with their arms up, under warm party light",
      foot: "Fireworks closing the party, seen from behind the guests",
      brand: "Table card with a QR code on a set table, by candlelight",
    },
    shotAlt: {
      guest: "The event camera open on a guest's phone",
      organizer: "The organizer panel showing the summary of one event",
      telao: "The big screen full of the event's revealed photos",
    },
  },
  nav: {
    language: "Language",
    sections: "Sections",
    menu: "Open the menu",
    close: "Close the menu",
    como: "How it works",
    produto: "Product",
    precos: "Pricing",
    faq: "FAQ",
    cta: "Message us",
    short: "Talk",
  },
  hero: {
    kicker: "Event photos, taken by the guests",
    title: ["The whole event,", "through ", "the people in it"],
    sub: "Guests scan a QR code, shoot from the browser, and the photos land in the event album and on the live screen. No app to install.",
    cta: "Message us",
    alt: "See how it works",
  },
  facts: [
    ["No app", "Guests open the camera in the browser, straight from the QR code."],
    ["Live", "Photos reach the album and the big screen while the event is happening."],
    ["Under control", "You moderate, hide, and decide when photos are revealed."],
  ],
  how: {
    label: "How it works",
    title: "Three steps, no downloads.",
    steps: [
      [
        "01",
        "Create the event",
        "Name, date, guest count, and when photos appear. One minute, and the printable QR is ready.",
      ],
      [
        "02",
        "Spread the QR",
        "On the invite, the table card, the screen. Point a camera at it and you are in, through the browser.",
      ],
      [
        "03",
        "Get the album",
        "Photos arrive grouped by hour, moderated, downloadable at original quality.",
      ],
    ],
  },
  guest: {
    label: "The guest",
    title: "A camera that takes seconds.",
    body: "Open, shoot, back to the event. The camera has a per-person quota, works inside WhatsApp and Instagram, and asks for no signup or install.",
    bullets: [
      "Enter by QR code or link",
      "Per-person photo quota",
      "Event album in the same place",
      "Works on 4G, no app",
    ],
  },
  organizer: {
    label: "The organizer",
    title: "One panel for every event you run.",
    body: "Create, follow, and moderate from anywhere. Each event has its invite, its album by hour, its moderation queue, and its screen.",
    bullets: [
      "Multi-event panel",
      "Invite with QR and short link",
      "Moderation and banning",
      "Reveal live or at the end",
    ],
  },
  panel: {
    label:
      "Illustration of the organizer panel: the event list with a wedding live now, two scheduled events and a finished birthday",
    title: "Events",
    newEvent: "New event",
    rail: ["Events", "Preferences"],
    groups: { live: "Live now", upcoming: "Upcoming", ended: "Finished" },
    status: { live: "Live", scheduled: "Scheduled", ended: "Finished" },
    remaining: "41h",
    events: {
      wedding: {
        name: "Ana & Rafael's Wedding",
        meta: "Sat, Sep 19 · 7:00 PM · 183 guests · 1,247 photos and videos",
        short: "1,247 photos and videos",
      },
      graduation: {
        name: "Medicine Class of 2026",
        meta: "Fri, Oct 2 · 9:00 PM · Guests 0/250",
        short: "Fri, Oct 2 · 9:00 PM",
      },
      company: {
        name: "Vertex Year-End Party",
        meta: "Fri, Dec 11 · 8:00 PM · Guests 0/100",
        short: "Fri, Dec 11 · 8:00 PM",
      },
      birthday: {
        name: "Beatriz · 30th Birthday",
        meta: "Sat, Aug 22 · 38 guests · 289 photos and videos",
        short: "289 photos and videos",
      },
    },
  },
  telao: {
    label: "Big screen",
    title: "The event watching itself happen.",
    body: "A full-screen page on the projector or TV. Photos appear as they are revealed, credited to whoever took them, readable from across the room.",
  },
  brand: {
    label: "White label",
    title: "Your name in front, ours out of the way.",
    body: "Event professionals deliver Kuori under their own name, logo, and colour. The interface is deliberately neutral: the colour on screen comes from the event's photos, not from our brand.",
  },
  pricing: {
    label: "Pricing",
    title: "Pay per event. No subscription.",
    sub: "Pick the band by expected guest count. You can move up later and pay only the difference, without creating another event.",
    tracks: [
      ["pessoal", "Personal"],
      ["empresarial", "Business"],
    ],
    trackNote: {
      pessoal: "Couples, birthdays, graduations, and trips. Self-serve up to 200 guests.",
      empresarial:
        "Planners, caterers, agencies, and companies. Self-serve up to 750 guests, invoiced.",
    },
    q1: "What kind of event?",
    q2: "How many guests?",
    upTo: "Up to {n} guests",
    over: "More than {n} guests",
    perPerson: " per person",
    freeNote: "To try it with a small group, no card required.",
    paidNote:
      "One-off payment for this event. You can move up a band later and pay only the difference.",
    contactNote:
      "On this track, events this size go through our team. Message us and we'll put the quote together.",
    contact: "Talk to us",
    free: "Free",
    includesLabel: "What's included",
    includes: {
      free: ["Event camera with a per-person quota", "Revealed album", "30-day retention"],
      paid: [
        "Short video up to 30s at 1080p",
        "Photos at original quality",
        "Custom table card",
        "Live big screen",
        "12 months of access, plus 12 to export",
      ],
    },
    everyone:
      "Reactions, comments, moderation, per-person filtering, and “my photos” apply to every event, paid or not.",
    disclaimer:
      "Prices in Brazilian reais, to be confirmed. Above the self-serve bands, we put the quote together with you.",
  },
  faq: {
    label: "FAQ",
    title: "What people ask before signing.",
    items: [
      [
        "Do guests have to install anything?",
        "No. They point a camera at the QR code, the browser opens, and the event camera is there. It works inside WhatsApp and Instagram.",
      ],
      [
        "Can I review photos before they appear?",
        "Yes. Reveal can be live or only at the end, and you can hide, delete, or ban an author at any time.",
      ],
      [
        "What if the event outgrows the band I bought?",
        "Move up a band and pay only the difference. Same event, same QR, same album.",
      ],
      [
        "Can I deliver it under my own brand?",
        "Yes. Name, logo, and accent colour are yours, on the invite, the album, and the screen.",
      ],
      [
        "How long do the photos stay available?",
        "On any paid band, 12 months of access plus 12 months to export. On the free plan, 30 days.",
      ],
      [
        "Does it work on bad connections?",
        "The camera is built for 4G. Photos queue and upload when the connection allows, without blocking the guest.",
      ],
    ],
  },
  foot: {
    cta: "Message us",
    note: "We reply the same business day.",
    rights: "2026 Kuori",
  },
};

export default content;
